import React, { useEffect } from "react";
import { observer } from "mobx-react";
import { useLocation } from "react-router-dom";
import { PageLayout } from "../../../../common/layout";
import { useRootStore } from "../../../../store/contexts";
import { GrossReceiptsStatusUpdate } from "./GrossReceiptsStatusUpdate";
import { GrossReceiptsStatusUpdateStore } from "../../store/GrossReceiptsStatusUpdateStore";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
} from "@mui/material";
import { useStore } from "../../../../hooks";
import { CallbackDataParams } from "../../../../store/types";

export const GrossReceiptsStatusUpdatePage = observer(() => {
  const rootStore = useRootStore();
  const { router, langStore } = rootStore;
  const location = useLocation();

  const id = router.params?.id as string;
  const path = `/grossReceiptsStatus/update/${id}`;

  // Get callback params from navigation state (when returning from "Add new")
  const navState = location.state as {
    callbackParams?: CallbackDataParams;
  } | null;
  const callbackParams = navState?.callbackParams;

  const store = useStore(
    path,
    () => new GrossReceiptsStatusUpdateStore(rootStore),
    { id }
  );

  // Setup callback data if returning from "Add new"
  useEffect(() => {
    if (callbackParams?.storeId && !store.callbackReturnPath) {
      store.callbackReturnPath = callbackParams.storeId;
      store.setCallbackParams(callbackParams);
    }
  }, [callbackParams, store]);

  return (
    <div>
      <PageLayout
        navigationElements={{
          links: store.navigationLinks,
          onNavigatedTo: (id) => {
            store.setCardState(id, true);
          },
        }}
        secondaryToolbox={
          <FormToolbox
            saveAndCloseLabel={langStore.saveEntity}
            closeLabel={langStore.cancelUpdatedEntity}
            saveForm={() => store.saveGrossReceiptsStatus()}
            saveDisabled={store.saveDisabled}
            closeForm={() => store.cancelGrossReceiptsStatus()}
          />
        }
      >
        <GrossReceiptsStatusUpdate
          grossReceiptsStatusUpdateStore={store}
          showToolbox={false}
        />
      </PageLayout>
      <Dialog
        open={store.validationErrorsDialogOpen}
        onClose={() => store.closeValidationErrorsDialog()}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{langStore.validationErrorsTitle}</DialogTitle>
        <DialogContent>
          <Box>
            {Object.keys(store.errors).map((key) => {
              const error = store.errors[key as keyof typeof store.errors];
              if (!error) return null;
              return (
                <Typography
                  key={key}
                  color="error"
                  variant="body2"
                  sx={{ mb: 1 }}
                >
                  • {error.fieldLabel}: {error.message}
                </Typography>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => store.closeValidationErrorsDialog()}
            color="primary"
          >
            {langStore.closeCommand}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
});
